/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, useParams } from 'react-router-dom'

import { Alert, Button, Checkbox, ConfirmModal, PageContainer, PageHeader, Toast } from '@/components/ui'
import { useApi } from '@/hooks'
import useToast from '@/hooks/useToast'
import { insightName } from '@/insights/insightCatalogue'
import logger from '@/utils/logger'

import { CompanyInfoCard } from './CompanyInfoCard'

import { LoadingPanel } from '../LoadingPanel'
import {
  APP_GRANT_LABELS,
  ConfirmDisableModal,
  PermissionsEditor,
  UnsavedChangesNote,
  buildUsage,
  disabledInUse,
  registeredInsights,
  normaliseValue,
  usePermissionsForm,
  useSaveConfirm,
  usersAndDashboardsLabel,
  type AdminCustomerDetail,
  type AdminCustomerProfile,
  type DisabledSet,
  type PermissionsValue,
  type UsageIndex,
} from '../permissions'

const log = logger({ category: 'AdminCompanyDetailPage' })

const users = (n: number) => `${String(n)} ${n === 1 ? 'user' : 'users'}`

interface SaveBody extends PermissionsValue {
  /** Also replace every user of the company with these lists. */
  applyToUsers?: boolean
}

type SaveResult = AdminCustomerProfile & { usersUpdated?: number }

interface ApplyToUsersConfirmProps {
  companyName: string
  userCount: number
  disabled: DisabledSet
  usage: UsageIndex
}

/** The question asked when "Apply to all users" is ticked, whatever else the save switches off. */
const ApplyToUsersConfirm: React.FC<ApplyToUsersConfirmProps> = ({ companyName, userCount, disabled, usage }) => {
  const lines = [
    ...disabled.grants.map((grant) => ({ key: grant, name: APP_GRANT_LABELS[grant] })),
    ...disabled.insights.map((slug) => ({ key: slug, name: insightName(slug) })),
  ]
  return (
    <div data-testid="apply-to-users-confirm">
      <p className="text-sm">
        This will replace the apps and insights of all {users(userCount)} of {companyName}.
      </p>
      {lines.length > 0 && (
        <>
          <p className="mt-3 mb-1 text-sm">The following are currently in use on a dashboard and will stop working:</p>
          <ul className="list-disc pl-5 text-sm">
            {lines.map((line) => (
              <li key={line.key}>
                {line.name} ({usersAndDashboardsLabel(usage.forSlug(line.key))})
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

const AdminCompanyDetailPage: React.FC = () => {
  const { api } = useApi()
  const { customerId = '' } = useParams<{ customerId: string }>()
  const { toast, show, showToast, hideToast } = useToast()

  const [detail, setDetail] = useState<AdminCustomerDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  // Off by default: pushing the lists onto every user is a deliberate, confirmed act.
  const [applyToUsers, setApplyToUsers] = useState(false)
  const [confirmingApply, setConfirmingApply] = useState(false)

  // useApi() hands back the module-level singleton, so this only re-runs on a route change.
  useEffect(() => {
    if (!customerId) return
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      try {
        const response = await api.get<AdminCustomerDetail>({
          path: `/app/admin/customers/${encodeURIComponent(customerId)}`,
          origin: 'AdminCompanyDetailPage#load',
        })
        if (cancelled) return
        if (response.error || !response.data) {
          setError(response.message || 'Could not load this company')
          return
        }
        setError('')
        setDetail(response.data)
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setError('Could not load this company')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [api, customerId])

  const baseline = useMemo<PermissionsValue | null>(
    () => (detail ? { grants: detail.profile.grants, insights: registeredInsights(detail.profile.insights) } : null),
    [detail]
  )
  const { value, setValue, isDirty, reset, commit } = usePermissionsForm<PermissionsValue>({
    baseline,
    normalise: normaliseValue,
  })
  const { pending, request, confirm, cancel } = useSaveConfirm()
  const form = value ?? baseline
  const usage = useMemo(() => buildUsage(detail?.users ?? []), [detail])

  const save = useCallback(async () => {
    if (!form) return
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await api.put<SaveResult, SaveBody>({
        path: `/app/admin/customers/${encodeURIComponent(customerId)}/profile`,
        origin: 'AdminCompanyDetailPage#save',
        body: { grants: form.grants, insights: form.insights, ...(applyToUsers ? { applyToUsers: true } : {}) },
      })
      if (response.error) {
        setSaveError(response.message || 'Could not save')
        return
      }
      commit(form)
      const { usersUpdated = 0, ...saved }: Partial<SaveResult> = response.data ?? {}
      const updatedAt = saved.updatedAt ?? new Date().toISOString()
      setDetail((previous) =>
        previous ? { ...previous, profile: { ...previous.profile, ...saved, updatedAt } } : previous
      )
      setApplyToUsers(false)
      showToast({
        header: 'Company permissions saved',
        body: `${usersUpdated > 0 ? `${users(usersUpdated)} updated. ` : ''}Changes apply at each user’s next sign-in or token refresh.`,
        position: 'end',
        success: true,
      })
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not save')
    } finally {
      setIsSaving(false)
    }
  }, [api, applyToUsers, commit, customerId, form, showToast])

  const handleSave = () => {
    if (!detail || !baseline || !form) return
    // Pushing onto every user always asks; a plain save asks only when it switches off something in use.
    if (applyToUsers) {
      setConfirmingApply(true)
      return
    }
    request(disabledInUse(baseline, form, usage), () => void save())
  }

  const handleCancel = () => {
    reset()
    setApplyToUsers(false)
    setSaveError('')
  }

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingPanel label="Loading company…" />
      </PageContainer>
    )
  }

  if (error || !detail || !form) {
    return (
      <PageContainer>
        <Link to="/admin/companies" className="link link-hover mb-2 inline-block text-sm">
          ← Companies
        </Link>
        <Alert message={error || 'Company not found'} type="error" />
      </PageContainer>
    )
  }

  const { customer, userCount, profile } = detail

  return (
    <PageContainer>
      <Link to="/admin/companies" className="link link-hover mb-2 inline-block text-sm">
        ← Companies
      </Link>
      <PageHeader title={customer.name} subtitle={`${String(userCount)} ${userCount === 1 ? 'user' : 'users'}`} />

      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-4">
          <CompanyInfoCard customer={customer} userCount={userCount} profileUpdatedAt={profile.updatedAt} />
        </div>

        <div className="flex flex-col gap-4">
          <section className="card bg-base-100 shadow">
            <div className="card-body">
              <h2 className="card-title text-base">Permissions</h2>
              <p className="text-sm text-base-content/70">
                Apps and insights switched off here are off for every user of this company. Saving for the first time
                creates the company&rsquo;s profile and applies immediately at users&rsquo; next token refresh.
              </p>

              <PermissionsEditor
                value={form}
                onChange={(next) => setValue(next)}
                cap={null}
                usage={usage}
                inUseLabel={usersAndDashboardsLabel}
                disabled={isSaving}
              />

              {saveError && <Alert message={saveError} type="error" />}

              {/* Also replaces every user's lists with these — the way an admin pushes a company
                  change out. Not part of the dirty check, so it works with the lists as they are. */}
              <div className="mt-4">
                <Checkbox
                  id="apply-to-users"
                  data-testid="apply-to-users"
                  label={`Apply to all ${users(userCount)}`}
                  checked={applyToUsers}
                  disabled={userCount === 0 || isSaving}
                  onChange={setApplyToUsers}
                />
              </div>

              <div className="card-actions mt-4 justify-end">
                <Button
                  handleClick={handleCancel}
                  isDisabled={(!isDirty && !applyToUsers) || isSaving}
                  variation="ghost"
                >
                  Cancel
                </Button>
                <Button
                  handleClick={handleSave}
                  isDisabled={(!isDirty && !applyToUsers) || isSaving}
                  variation="primary"
                >
                  {isSaving ? (
                    <>
                      <span className="loading loading-spinner loading-xs" />
                      Saving…
                    </>
                  ) : (
                    'Save permissions'
                  )}
                </Button>
              </div>
              <UnsavedChangesNote show={isDirty} />

              <ConfirmDisableModal
                disabled={pending}
                usage={usage}
                inUseLabel={usersAndDashboardsLabel}
                isSaving={isSaving}
                onConfirm={confirm}
                onCancel={cancel}
              />
              {/* The shared Modal keeps its body mounted while closed; only mount while asking. */}
              {confirmingApply && baseline && (
                <ConfirmModal
                  show
                  title={`Apply to all ${users(userCount)}?`}
                  variation="warning"
                  confirmLabel="Save and apply"
                  cancelLabel="Keep editing"
                  isLoading={isSaving}
                  onConfirm={() => {
                    setConfirmingApply(false)
                    void save()
                  }}
                  onCancel={() => setConfirmingApply(false)}
                  body={
                    <ApplyToUsersConfirm
                      companyName={customer.name}
                      userCount={userCount}
                      disabled={disabledInUse(baseline, form, usage)}
                      usage={usage}
                    />
                  }
                />
              )}
            </div>
          </section>
        </div>
      </div>

      <Toast
        show={show}
        onClose={hideToast}
        header={toast.header}
        body={toast.body}
        position={toast.position}
        success={toast.success}
        info={toast.info}
        warning={toast.warning}
      />
    </PageContainer>
  )
}

export default AdminCompanyDetailPage
